import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ArrowLeft,
  Rocket,
  Upload,
  Sparkles,
  Search,
  AlertTriangle
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  icon: React.ElementType;
  items: FAQItem[];
}

const faqSections: FAQSection[] = [
  {
    title: 'Getting Started',
    icon: Rocket,
    items: [
      {
        question: 'What is PageMind?',
        answer: 'PageMind is a multi-agent AI system for website analysis and content optimization. It helps you generate AEO (Answer Engine Optimization) content, understand customer behavior, and improve your website\'s visibility in AI-powered search engines.'
      },
      {
        question: 'What are the different user roles?',
        answer: 'PageMind has three main roles: Super Admin (full platform access, sector/site management), Content Manager (feed imports, content review, AEO generation), and Site Owner (analytics, insights, basic content management). Each role has access to different features based on their responsibilities.'
      },
      {
        question: 'What is a "Sector"?',
        answer: 'A Sector represents an industry or business domain (e.g., "Industrial Motors", "Fashion Retail"). Sectors contain shared research, personas, and category knowledge that applies to all sites within that industry. This allows PageMind to generate more relevant and accurate content.'
      },
      {
        question: 'How do I get started with a new website?',
        answer: 'The typical workflow is: 1) Create or assign a sector for your industry, 2) Run sector research to build category knowledge, 3) Create your site and assign it to the sector, 4) Import your product feed, 5) Run the AEO pipeline to generate content. See our complete Onboarding Guide for detailed steps.'
      }
    ]
  },
  {
    title: 'Feed Imports',
    icon: Upload,
    items: [
      {
        question: 'What feed formats does PageMind support?',
        answer: 'PageMind supports CSV, TSV, and XML (Google Shopping format) feeds. The system automatically detects the format and column mappings during the analysis phase.'
      },
      {
        question: 'What fields should my feed include?',
        answer: 'At minimum, your feed should include: Product ID/SKU, Product name, Description, and Category. For better results, also include: Technical specifications, Price, Images (URLs), Brand, and any relevant attributes for your industry.'
      },
      {
        question: 'How does feed enrichment work?',
        answer: 'AI Enrichment uses language models to extract additional attributes from product descriptions, normalize values, and compute benefit scores. It identifies hidden specifications and standardizes data for better content generation.'
      },
      {
        question: 'What is the Quality Gate?',
        answer: 'The Quality Gate is an optional filter that skips products missing critical data. This ensures only products with sufficient information get AEO content generated, preventing low-quality outputs.'
      },
      {
        question: 'How often should I re-import my feed?',
        answer: 'If your catalog changes frequently, set up daily or weekly scheduled imports. The system performs incremental processing, only updating products that have changed since the last import.'
      }
    ]
  },
  {
    title: 'AEO Generation',
    icon: Sparkles,
    items: [
      {
        question: 'What is AEO content?',
        answer: 'AEO (Answer Engine Optimization) content is optimized for AI-powered search engines and voice assistants. It includes structured Q&A, benefit explanations, persona matching, and executive summaries that help AI systems understand and surface your products.'
      },
      {
        question: 'What does the AEO pipeline generate?',
        answer: 'For each product: Executive summary, Best-for badges (persona matches), Benefit explanations grounded in specifications, Q&A sections, Comparison suggestions, and Structured data. For categories: Buyer guides, Use-case segmentation, and Category FAQs.'
      },
      {
        question: 'How do I ensure AEO quality?',
        answer: 'Quality depends on: 1) Good input data in your feed, 2) Category research (perceived benefits, attribute intelligence), 3) Demand research (real customer questions), 4) Review and approval of generated content. The validation system flags potential hallucinations automatically.'
      },
      {
        question: 'What are "perceived benefits"?',
        answer: 'Perceived benefits bridge technical specifications to customer needs. For example, "high torque" (spec) becomes "powerful lifting capability" (benefit). These are discovered through category research and help generate more persuasive content.'
      },
      {
        question: 'How does persona matching work?',
        answer: 'PageMind discovers relevant buyer personas during category research (e.g., "Fleet Manager", "DIY Enthusiast"). Products are matched to personas based on their attributes and benefit scores, enabling "Best for X" badges.'
      }
    ]
  },
  {
    title: 'Discovery Service',
    icon: Search,
    items: [
      {
        question: 'What is the Discovery service?',
        answer: 'Discovery is PageMind\'s semantic search system. It uses vector embeddings to find products and content based on natural language queries, enabling conversational commerce and AI assistant integration.'
      },
      {
        question: 'How do I test Discovery queries?',
        answer: 'Go to Robot Diagnostics > Chat Test. Enter natural language queries like "12V motor under 2kg for agricultural equipment" and see which products the system returns with relevance explanations.'
      },
      {
        question: 'Why isn\'t Discovery returning results?',
        answer: 'Check: 1) Knowledge Base has entries for your products, 2) Embedding generation completed in the feed import, 3) Your query terms exist in the indexed content. Try simpler queries first to narrow down the issue.'
      },
      {
        question: 'Can I enable a customer-facing chatbot?',
        answer: 'Yes, once your content is indexed, you can enable the chatbot widget in Site Settings. The chatbot answers product questions, provides recommendations, and handles comparison queries using your AEO content.'
      }
    ]
  },
  {
    title: 'Troubleshooting',
    icon: AlertTriangle,
    items: [
      {
        question: 'Feed import stuck at "Enrichment"',
        answer: 'This usually means: 1) OpenAI API key needs configuration (check with admin), 2) Products have insufficient description text, 3) Rate limiting is occurring. Check the import logs for specific error messages.'
      },
      {
        question: 'AEO content has incorrect specifications',
        answer: 'This can happen when attribute intelligence is missing. Go to Research > Category Perception Research, select your category, and click "Research Attribute Values". Then regenerate the affected content.'
      },
      {
        question: 'Personas don\'t match products well',
        answer: 'Run category research to discover relevant personas for your industry. Check that benefit scores are computed for products. Review the persona-benefit mappings in the Research section.'
      },
      {
        question: 'Content shows "hallucination" warnings',
        answer: 'The hallucination detection system flags content that may include made-up specifications. Review flagged content in Enrichment Review and correct or regenerate as needed.'
      },
      {
        question: 'How do I get support?',
        answer: 'Use My Suggestions to submit questions or report issues. For urgent problems, contact your PageMind administrator. Include specific error messages and the steps you took when reporting issues.'
      }
    ]
  }
];

export default function FAQ() {
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <Link
          to="/help"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Help Center
        </Link>
        <h1 className="text-3xl font-bold mb-2">Frequently Asked Questions</h1>
        <p className="text-muted-foreground">
          Find answers to common questions about using PageMind
        </p>
      </div>

      <div className="space-y-6">
        {faqSections.map((section) => (
          <Card key={section.title}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <section.icon className="h-5 w-5 text-primary" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-8 bg-muted/50">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Can't find what you're looking for?{' '}
            <Link to="/help/guides" className="text-primary hover:underline">
              Check our User Guides
            </Link>{' '}
            for detailed walkthroughs, or{' '}
            <Link to="/my-suggestions" className="text-primary hover:underline">
              submit a question
            </Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
